import { useLanguage } from "@/contexts/LanguageContext";
import { useNavigate, useLocation } from "react-router-dom";
import { usePortalBase } from "@/hooks/usePortalBase";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { AlertTriangle, ArrowUpCircle } from "lucide-react";

interface UpgradeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type: "unit" | "user";
  current: number;
  limit: number;
  requested?: number; // Number of items user tried to add
}

export function UpgradeModal({ open, onOpenChange, type, current, limit, requested }: UpgradeModalProps) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const portalBase = usePortalBase();

  const usage = limit > 0 ? (current / limit) * 100 : 100;
  const typeLabel = type === "unit" 
    ? { ar: "الوحدات", en: "units" } 
    : { ar: "المستخدمين", en: "users" };

  const handleUpgrade = () => {
    onOpenChange(false);
    // Pass the return URL so user can be redirected back after upgrade
    navigate(`${portalBase}/my-subscription`, {
      state: { returnUrl: location.pathname },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md text-center">
        <DialogHeader className="space-y-4">
          <div className="mx-auto w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center">
            <AlertTriangle className="h-8 w-8 text-amber-600" />
          </div>
          <DialogTitle className="text-xl">
            {t("تم الوصول لحد الباقة!", "Plan Limit Reached!")}
          </DialogTitle>
          <DialogDescription>
            {requested && requested > 1 ? (
              t(
                `حاولت إضافة ${requested} ${t(typeLabel.ar, typeLabel.en)} ولكن الحد الأقصى في باقتك الحالية هو ${limit}`,
                `You tried to add ${requested} ${t(typeLabel.ar, typeLabel.en)} but your current plan only allows ${limit}`
              )
            ) : (
              t(
                `لقد وصلت إلى الحد الأقصى من ${t(typeLabel.ar, typeLabel.en)} في باقتك الحالية`,
                `You've reached the maximum number of ${t(typeLabel.ar, typeLabel.en)} in your current plan`
              )
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="bg-muted rounded-lg p-4 space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{t("الحالي", "Current")}</span>
              <span className="font-bold text-primary">{current}</span>
            </div>
            <Progress value={usage} className="h-2" />
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{t("الحد الأقصى", "Limit")}</span>
              <span className="font-bold">{limit}</span>
            </div>
            {requested && requested > 1 && (
              <div className="flex justify-between text-sm border-t pt-2">
                <span className="text-muted-foreground">{t("المطلوب إضافتها", "Requested")}</span>
                <span className="font-bold text-destructive">+{requested}</span>
              </div>
            )}
          </div>

          <p className="text-sm text-muted-foreground">
            {t(
              "قم بترقية باقتك لإضافة المزيد وفتح مميزات إضافية",
              "Upgrade your plan to add more and unlock additional features"
            )}
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <Button onClick={handleUpgrade} className="w-full gap-2">
            <ArrowUpCircle className="h-4 w-4" />
            {t("ترقية الباقة", "Upgrade Plan")}
          </Button>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("إغلاق", "Close")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}